import { Currency } from '@sequencehq/api'
import { useEnabledCurrencies } from 'SettingsV2/hooks/useCurrencies'
import { useMemo, useState } from 'react'
import { InvoiceSummaryStat } from './useInvoiceSummaryStats'

type CurrencyNavState = {
  activeCurrency: Currency | undefined
  showCurrencyNav: boolean
  showCurrencyInFooter: boolean
  goToPreviousCurrency: () => void
  goToNextCurrency: () => void
}

export const useInvoiceStatsCurrencyNav = (
  stats: InvoiceSummaryStat[]
): CurrencyNavState => {
  const [selectedCurrency, setSelectedCurrency] = useState<Currency>()

  const enabledCurrenciesRes = useEnabledCurrencies()
  const enabledCurrencies =
    enabledCurrenciesRes.status === 'SUCCESS'
      ? enabledCurrenciesRes.enabledCurrencies
      : []
  const defaultCurrency = enabledCurrencies.at(0)
  const showCurrencyInFooter = enabledCurrencies.length > 1

  // Sum each currency's gross total and invoice count across all cards, so we
  // can rank currencies and lead with the most significant one.
  const totalsByCurrency = useMemo(
    () =>
      stats
        .flatMap(stat => stat.totals)
        .reduce<Record<string, { grossTotal: number; invoiceCount: number }>>(
          (acc, { currency, grossTotal, invoiceCount }) => ({
            ...acc,
            [currency]: {
              grossTotal: (acc[currency]?.grossTotal ?? 0) + grossTotal,
              invoiceCount: (acc[currency]?.invoiceCount ?? 0) + invoiceCount
            }
          }),
          {}
        ),
    [stats]
  )

  // Rank by gross total, then invoice count, then name — so paging leads with a
  // significant currency rather than a near-empty one.
  const currencies = useMemo(
    () =>
      (Object.keys(totalsByCurrency) as Currency[]).sort(
        (a, b) =>
          totalsByCurrency[b].grossTotal - totalsByCurrency[a].grossTotal ||
          totalsByCurrency[b].invoiceCount - totalsByCurrency[a].invoiceCount ||
          a.localeCompare(b)
      ),
    [totalsByCurrency]
  )

  const isAvailable = (currency: Currency | undefined): currency is Currency =>
    currency !== undefined && currencies.includes(currency)

  // Open on the account default currency when it has invoices here, otherwise
  // on the highest-value currency. With no invoices at all, still fall back to
  // the account default so the cards read as a formatted zero rather than a dash.
  const preferredCurrency = isAvailable(defaultCurrency)
    ? defaultCurrency
    : (currencies.at(0) ?? defaultCurrency)
  const activeCurrency = isAvailable(selectedCurrency)
    ? selectedCurrency
    : preferredCurrency

  const activeIndex = activeCurrency ? currencies.indexOf(activeCurrency) : 0
  const showCurrencyNav = currencies.length > 1

  const goToPreviousCurrency = () =>
    setSelectedCurrency(
      currencies[(activeIndex - 1 + currencies.length) % currencies.length]
    )
  const goToNextCurrency = () =>
    setSelectedCurrency(currencies[(activeIndex + 1) % currencies.length])

  return {
    activeCurrency,
    showCurrencyNav,
    showCurrencyInFooter,
    goToPreviousCurrency,
    goToNextCurrency
  }
}
